// @flow
import * as React from 'react'
import styled from 'styled-components'
import { P } from '../shared/typography'
import { CTAPrimary } from '../shared/buttons'
import { colors } from '../shared/colors'
import { ClearButton } from '../shared/icons/ClearButton'
import { config } from 'react-awesome-styled-grid'
import { events } from '@services/analytics'

type PropsType = {
  className?: string,
  errorMessage?: string,
  formId: string,
  name: string,
  onChange: (event: SyntheticInputEvent<*>) => void,
  onClickApply: (event: SyntheticInputEvent<*>) => void,
  clearPromo?: () => void,
  placeholder: string,
  spellCheck?: boolean,
  value?: string | null,
  color?: string
}

export const InputWithApplyCta = (props: PropsType): React.Node => {
  const [getValue, setValue] = React.useState<string | null>(null)
  React.useEffect((): void => {
    if (props.value) {
      setValue(props.value)
    }
  })

  const apply = React.useCallback((event: SyntheticInputEvent<*>): void => {
    if (getValue) {
      events.user_entered_promo_code(getValue)
    }
    props.onClickApply(event)
  })

  return (
    <Container>
      <Flex>
        <InputContainer>
          <Input
            aria-label={props.placeholder}
            className={props.className || 'input-with-apply'}
            form={props.formId}
            isError={!!props.errorMessage}
            name={props.name}
            onChange={(event: SyntheticInputEvent<*>): void => {
              setValue(event.target.value)
              props.onChange(event)
            }}
            placeholder={props.placeholder}
            type="text"
            value={getValue || ''}
            spellCheck={props.spellCheck || false}
            data-form-type="other" // Dashlane
            data-lpignore="true" // Lastpass
            autoComplete="off" // All others
            color={props.color}
            borderColor={props.color}
          />
          <ErrorXIconContainer onClick={(): void => {
            setValue(null)
            props.clearPromo && props.clearPromo()
          }}>
            {!!props.errorMessage && <ClearButton/>}
          </ErrorXIconContainer>
        </InputContainer>

        <ApplyButton
          isActive={!!getValue}
          onClick={apply}
          borderColor={props.color}
          color={props.color}
          backgroundColor={colors.orange}
        >
          APPLY
        </ApplyButton>
      </Flex>

      {!!props.errorMessage && (
        <ErrorContainer>
          <ErrorText>{props.errorMessage}</ErrorText>
        </ErrorContainer>
      )}
    </Container>
  )
}

const Container = styled.div`
  margin: 0 0 15px 0;
  display: flex;
  justify-content: center;
  width: 100%;
  flex-direction: column;
`
const InputContainer = styled.div`
  border-radius: 10px;
  display: flex;
  height: 40px;
  width: 100%;
  position: relative;
  flex-grow: 1;
`
const Input = styled.input`
  border: 1px solid ${(props: any): string => props.borderColor || colors.sand};
  border-radius: 8px;
  box-sizing: border-box;
  color: ${(props: any): string => props.color || colors.sand};
  font-family: "Good Sans Light";
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 150%;
  outline: none;
  padding: 6px 10px 6px 12px;
  width: 100%;
  background: transparent;

  ${(props: { isError: boolean }): ?string => {
  if (props.isError) {
    return `
        color: ${colors.red};
        background: ${colors.white15};
        outline: 1px solid ${colors.red};
        outline-offset: -4px;
      `
  }
}}
  &:focus {
    background: ${colors.white15};
    color: ${colors.white};
    ::placeholder {
      color: ${colors.white};
    }
  }
  ::placeholder {
    color: ${(props: any): string => props.color || colors.sand};
  }

`
const Flex = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
`
const ErrorContainer = styled.div`
  display: flex;
  margin: 10px 0 0 0;
  justify-content: center;
`
const ApplyButton = styled(CTAPrimary)`
  font-family: Good Sans Bold;
  font-size: 12px;
  padding: 11px 32px 13px;
  letter-spacing: 0.03em;
  height: 40px;
  width: 114px;
  min-width: 114px;
  margin-left: 12px;
  color: ${(props: any): string => props.color || colors.sand};
  border: 1px solid ${(props: any): string => props.borderColor || colors.sand};
  ${(props: any): string => config(props).media.sm`
    margin: 0px 0 0 20px;
  `}

  ${(props: { isActive: boolean, backgroundColor: string }): ?string => {
  if (props.isActive) {
    return `
      border: 1px solid ${props.backgroundColor};
      background-color: ${props.backgroundColor};
      color: ${colors.white};
    `
  } else {
    return `
        background-color: transparent;
        cursor: not-allowed;
      `
  }
}}

`
const ErrorText = styled(P)`
  font-size: 12px;
  line-height: 18px;
  text-align: left;
  width: 100%;
`
const ErrorXIconContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;

  ${(props: any): string => config(props).media.sm`
    right: 8px;
  `}

  ${(props: any): string => config(props).media.md`
    right: 15px;
  `}

  ${(props: any): string => config(props).media.lg`
    right: 10px;
  `}

  ${(props: any): string => config(props).media.xl`
    right: 10px;
  `}
`
